import { UserService } from "../../core/authentication/services/user.service";
import { startWith, takeUntil } from "rxjs/operators";
import {
  Component,
  computed,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";

import { Location, NgClass } from "@angular/common";
import { Subject } from "rxjs";
import { AuthService } from "../../core/authentication/services/auth.service";
import { User } from "../../shared/models/user.model";
import { Router, RouterLink, RouterLinkActive } from "@angular/router";
import { BreakpointObserver } from "@angular/cdk/layout";
import { RolePermissionService } from "../../core/authentication/services/role-permission.service";
import { CommunityService } from "../../modules/community/community.service";
import { ActiveNavItemService } from "./active-nav-item.service";
import { toSignal } from "@angular/core/rxjs-interop";
import { LogService } from "../../shared/service/logging/log.service";
import { DisplayNamePipe } from "../../shared/pipes/display-name.pipe";
import { ProfileImageComponent } from "../../shared/modules/profile-image/profile-image-component";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";

export enum NavItems {
  None,
  Home,
  Events,
  Communities,
  Networks,
  Manage,
}

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
  standalone: true,
  imports: [
    RouterLink,
    NgClass,
    RouterLinkActive,
    BsDropdownModule,
    ProfileImageComponent,
    DisplayNamePipe,
  ],
})
export class AppHeaderComponent implements OnInit, OnDestroy {
  isPWAonIOS: boolean;

  @ViewChild("dropdown") dropdownElement: ElementRef;

  // Use signals
  public hasAdminAccess = this.rolePermissionService.hasAdminAccess;
  public hasCoreMemberAccess = this.rolePermissionService.hasCoreMemberAccess;
  public hasCapgeminiAccess = this.rolePermissionService.hasCapgeminiAccess;
  public mayManage = computed(() => this.hasCoreMemberAccess());

  public userInfo = this.userService.userInfo;
  public isLoggedIn = this.authService.isLoggedIn;
  public firebaseUserInfo = this.authService.firebaseUserInfo;

  public sidenavOpen = signal(false);
  public dropdownOpen = signal(false);
  public navItemEnum = NavItems;

  private ngUnsubscribe = new Subject();

  private getActiveNavItem = toSignal(
    this.activeNavItemService.activeNavItem$.pipe(takeUntil(this.ngUnsubscribe))
  );

  public activeNavItem = computed(() => this.getActiveNavItem());

  constructor(
    public authService: AuthService,
    private rolePermissionService: RolePermissionService,
    private _location: Location,
    private userService: UserService,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private activeNavItemService: ActiveNavItemService,
    private logger: LogService
  ) {}

  ngOnInit() {
    this.breakpointObserver
      .observe(["(max-width: 1239px)"])
      .pipe(
        startWith({ matches: window.innerWidth <= 1239 }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((_) => {
        if (this.sidenavOpen()) {
          this.closeSidenav();
        }
      });

    // TODO: Debug printing added, won't be logged in Prod, but maybe remove?
    this.userService.userInfo$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((userInfo) => {
        // Use signals to get role states directly
        const admin = this.hasAdminAccess();
        const coreMember = this.hasAdminAccess();
        const capgemini = this.hasCapgeminiAccess();
        this.logger.info("UserInfo: ", userInfo);

        this.logger.info("Admin:", admin);
        this.logger.info("Core Member:", coreMember);
        this.logger.info("Capgemini User:", capgemini);

        this.mustUpdateCity(capgemini, userInfo);
      });

    this.isPWAonIOS =
      navigator.userAgent.match(/(?=(iPod|iPhone))(?=.*AppleWebKit)/) != null &&
      window.matchMedia("(display-mode: standalone)").matches;
  }

  mustUpdateCity(isCapgeminiUser: boolean, userInfo: User | null): void {
    if (isCapgeminiUser && userInfo?.city === null) {
      this.router.navigate(["/my-account"]);
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  public redirectToLogin(): void {
    this.closeSidenav();
    this.authService.redirectToLogin();
  }

  public logout(): void {
    this.closeSidenav();
    this.authService.logout();
  }

  public isFrontPage(): boolean {
    return this._location.isCurrentPathEqualTo("");
  }

  public toggleDropdown() {
    this.dropdownOpen.set(!this.dropdownOpen());
  }

  turnArrow() {
    this.toggleDropdown();
    var arrow = document.getElementById("nav-item-isloggedin-dropdown");
    if (!arrow) {
      return;
    }
    arrow.style.transitionDuration = "0.2s";
    this.dropdownOpen()
      ? (arrow.style.transform = "rotateX(180deg)")
      : (arrow.style.transform = "rotateX(0deg)");
  }

  @HostListener("document:click", ["$event"])
  handleClickOutside(event: Event): void {
    const clickedInside = this.dropdownElement?.nativeElement?.contains(
      event.target
    );
    if (!clickedInside && this.dropdownOpen()) {
      this.turnArrow();
    }
  }

  openSidenav(): void {
    document.documentElement?.classList.add("no-scroll");
    this.sidenavOpen.set(true);
  }

  closeSidenav(): void {
    document.documentElement?.classList.remove("no-scroll");
    this.sidenavOpen.set(false);
  }
}
